import { createSelector } from 'reselect'

// project
import { isEmpty } from 'lodash'
import { NAME } from './constants'

export const getApplications = createSelector(
  (state) => state[NAME],
  (module) => module.applications ?? []
)

export const isInitialized = createSelector(
  (state) => state[NAME],
  (module) => module.initialized
)

export const getApplicationById = (id, state) =>
  createSelector(
    () => state[NAME].list,
    (list) => list.find((item) => item.Id === id)
  )(state)

export const getBenefitEffectiveDate = createSelector(
  (state) => state[NAME],
  (applicationEngine) => applicationEngine.benefitEffectiveDate
)

export const getSortBy = createSelector(
  (state) => state[NAME].sortBy,
  (sortBy) => sortBy
)

export const getSortDescending = createSelector(
  (state) => state[NAME].sortDescending,
  (sortDescending) => sortDescending
)

const filterHandler = {
  companyId: (application, { companyId }) =>
    companyId?.length > 0
      ? companyId.includes(application.ClientPublicKey)
      : true,
  keyword: (application, { keyword }) => {
    const searchWord = keyword.toLowerCase()
    const employeeName = application.EmployeeName.toLowerCase()
    const employeeMatch = employeeName.includes(searchWord)
    if (employeeMatch) return true
    return application.Persons.some((p) => {
      const applicantName = p.FullName.toLowerCase()
      return applicantName.includes(searchWord)
    })
  },
  Active: (application) => application.IsActive,
  State: (application, { filters: { State } }) =>
    State?.length > 0
      ? State.map((state) => state.value).includes(application.StateProvince)
      : true,
  BenefitEffectiveDate: (
    application,
    { filters: { BenefitEffectiveDate }, benefitEffectiveDate }
  ) => {
    const selectedDate = BenefitEffectiveDate ?? benefitEffectiveDate
    return selectedDate
      ? application.BenefitEffectiveDate === selectedDate
      : true
  },
  CarrierName: (application, { filters: { CarrierName } }) =>
    CarrierName?.length > 0
      ? CarrierName?.map((option) => option.value).includes(
          application.CarrierName
        )
      : true,
  CompanyName: (application, { filters: { CompanyName } }) =>
    CompanyName?.length > 0
      ? CompanyName?.map((option) => option.value).includes(
          application.CompanyName
        )
      : true,
  Assignee: (application, { assigneeFilter }) => {
    if (isEmpty(assigneeFilter)) return true

    if (assigneeFilter.length > 0) {
      return assigneeFilter
        .map((option) => option?.value)
        .includes(application?.Assignee)
    }

    return false
  },
  Status: (application, { filters }) => {
    const { WorkerSubmitted, HixmeSubmitted, Validated, Other } = filters
    const statuses = {
      'Worker Submitted': WorkerSubmitted,
      'Hixme Submitted': HixmeSubmitted,
      Validated: Validated,
    }
    const isOther = (status) => !Object.keys(statuses).includes(status)

    if (Other && isOther(application.CarrierApplication.Status)) {
      return true
    }

    return Object.entries(statuses).some(
      ([status, value]) =>
        value && application.CarrierApplication.Status === status
    )
  },
  NeedsCancellation: (application, { needsCancellation }) =>
    needsCancellation
      ? !application.CarrierApplication.IsSameCarrier &&
        !application.CarrierApplication.CancellationSent
      : true,
  NeedsAttention: (application, { needsAttention }) =>
    needsAttention ? application.CarrierApplication.NeedsAttention : true,
  CartOpen: (application, { cartOpen }) =>
    cartOpen ? !application.CartCommitted : true,
  SameCarrier: (application, { sameCarrier }) =>
    sameCarrier ? application.CarrierApplication.IsSameCarrier : true,
  Users: (application, { users }) => {
    const bothFiltersSameValue = users.nonTestUsers === users.testUsers
    if (bothFiltersSameValue) return true
    return users.testUsers ? application.IsTestUser : !application.IsTestUser
  },
  commissionPartnerFilter: (
    application,
    {
      commissionFilters: {
        HasCommissionPartner,
        NoCommissionPartner,
        CommissionPartnerSearch,
      },
    }
  ) => {
    const appCommission = application.CommissionPartner ?? ''
    let hasCommissionCheck = false
    let noCommissionCheck = false

    if (HasCommissionPartner) {
      if (appCommission) {
        hasCommissionCheck = CommissionPartnerSearch
          ? appCommission
              .toLowerCase()
              .indexOf(CommissionPartnerSearch.toLowerCase()) > -1
          : true
      }
    }

    if (NoCommissionPartner) {
      noCommissionCheck = !appCommission
    }

    return hasCommissionCheck || noCommissionCheck
  },
  ShowUnassigned: (application, { showUnassigned }) =>
    showUnassigned ? isEmpty(application.Assignee) : true,
  carrierPlanChange: (application, { carrierPlanChange }) => {
    if (isEmpty(carrierPlanChange)) return true

    return carrierPlanChange.length > 0
      ? carrierPlanChange
          .map((item) => item.value)
          .includes(application.CarrierPlanChange)
      : true
  },
}

export const filteredApplicationsList = createSelector(
  (state) => getApplications(state),
  (state) => state[NAME].keyword,
  (state) => getFilters(state),
  (state) => state[NAME].companyId,
  (state) => state[NAME].needsAttention,
  (state) => state[NAME].needsCancellation,
  (state) => state[NAME].cartOpen,
  (state) => state[NAME].sameCarrier,
  (state) => state[NAME].nonTestUsers,
  (state) => state[NAME].testUsers,
  (state) => state[NAME].commissionFilters,
  (state) => state[NAME].assigneeFilter,
  (state) => state[NAME].showUnassigned,
  (state) => state[NAME].carrierPlanChange,
  (
    list,
    keyword,
    filters,
    companyId,
    needsAttention,
    needsCancellation,
    cartOpen,
    sameCarrier,
    nonTestUsers,
    testUsers,
    commissionFilters,
    assigneeFilter,
    showUnassigned,
    carrierPlanChange
  ) => {
    if (!list) return []
    const applicationFilters = {
      keyword,
      filters,
      companyId,
      needsAttention,
      needsCancellation,
      cartOpen,
      sameCarrier,
      users: {
        nonTestUsers,
        testUsers,
      },
      commissionFilters,
      assigneeFilter,
      showUnassigned,
      carrierPlanChange,
    }
    return (
      list.filter((application) =>
        Object.values(filterHandler).every((handler) =>
          handler(application, applicationFilters)
        )
      ) ?? []
    )
  }
)

export const getBenefitEffectiveDateForQuery = createSelector(
  (state) => state[NAME],
  (module) => module.benefitEffectiveDateForQuery || null
)

export const getBenefitEffectiveDateRangeForQuery = createSelector(
  (state) => state[NAME],
  (module) => module.benefitEffectiveDateRangeForQuery || null
)

export const getBenefitsEffectiveDates = createSelector(
  (state) => getApplications(state),
  (list) => {
    const allBenefits =
      (list && list.map((item) => item.BenefitEffectiveDate)) || []
    return [...new Set(allBenefits)]
  }
)

export const getCarriers = createSelector(
  (state) => getApplications(state),
  getBenefitEffectiveDate,
  (list, bed) => {
    const filteredByBed = list
      .filter((item) => (bed ? item.BenefitEffectiveDate === bed : item))
      .map((item) => item.CarrierName)
    return [...new Set(filteredByBed.sort())]
  }
)

export const getCompanyNames = createSelector(
  (state) => getApplications(state),
  getBenefitEffectiveDate,
  (list) => {
    const filteredByBed = list
      // .filter((item) => (bed ? item.BenefitEffectiveDate === bed : item))
      .map((item) => item.CompanyName)
    return [...new Set(filteredByBed.sort())]
  }
)

export const getFilters = createSelector(
  (state) => state[NAME],
  (module) => module.updateFilter
)

export const getNeedsAttention = createSelector(
  (state) => state[NAME],
  (module) => module.needsAttention
)

export const getNeedsCancellation = createSelector(
  (state) => state[NAME],
  (module) => module.needsCancellation
)

export const getCartOpen = createSelector(
  (state) => state[NAME],
  (module) => module.cartOpen
)

export const getSameCarrier = createSelector(
  (state) => state[NAME],
  (module) => module.sameCarrier
)
export const getCarrierPlanChange = createSelector(
  (state) => state[NAME],
  (module) => module.carrierPlanChange
)
export const getIsTestUser = createSelector(
  (state) => state[NAME],
  (module) => module.testUsers
)

export const getIsNonTestUser = createSelector(
  (state) => state[NAME],
  (module) => module.nonTestUsers
)

export const isViewAllSelected = createSelector(
  (state) => state[NAME],
  (state) => state.isViewAllSelected
)

export const recordPointerSelector = createSelector(
  (state) => state[NAME],
  (temp) => temp.recordPointer
)

export const hasMore = createSelector(
  filteredApplicationsList,
  recordPointerSelector,
  isViewAllSelected,
  (employeeList, recordPointer, isViewAllSelected) =>
    employeeList.length > recordPointer && !isViewAllSelected
)

export const keyword = createSelector(
  (state) => state[NAME],
  (state) => state.keyword
)

export const getCompanyId = createSelector(
  (state) => state[NAME],
  (state) => state.companyId
)

export const showFilters = createSelector(
  (state) => state[NAME],
  (module) => module.showFilters
)

export const agingList = createSelector(
  (state) => state[NAME].list,
  (employeeList) => {
    const allAgingValues = employeeList.map((item) => item.Aging)
    const emptyOption = [{ id: '', name: 'Select' }]
    // new es6 method of extracting unique values using spreading and Set
    const uniqueOptions = [...new Set(allAgingValues)].map((value) => ({
      id: value,
      name: value,
    }))
    return [...emptyOption, ...uniqueOptions]
  }
)

export const getCommissionPartnerFilter = createSelector(
  (state) => state[NAME],
  (module) => module.commissionFilters
)

export const getAssigneeFilter = createSelector(
  (state) => state[NAME],
  (module) => module.assigneeFilter
)

export const getSelectedApplications = createSelector(
  (state) => state[NAME],
  (module) => module.selectedApplications
)
